import { useState } from "react"

import type { MapClickPoint } from "./MapView"

type Props = {
  pin: MapClickPoint | null
  onSubmit: (body: {
    lat: number
    lon: number
    chain: string | null
    name: string | null
  }) => Promise<void>
  onClose: () => void
}

const CHAINS = [
  "Biedronka",
  "Lidl",
  "Kaufland",
  "Żabka",
  "Auchan",
  "Carrefour",
  "Dino",
  "Inne / Nieznane",
]

export function AddAutomatForm({ pin, onSubmit, onClose }: Props) {
  const [chain, setChain] = useState<string>("")
  const [name, setName] = useState<string>("")
  const [busy, setBusy] = useState(false)

  const useGeo = () => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // We send the event up via onSubmit path without pin; but add form needs a pin
        // so simulate a synthetic map-click: we bypass by submitting directly.
        submitWith(pos.coords.latitude, pos.coords.longitude)
      },
      () => {
        // silently ignore — user can still tap on map
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }

  const submitWith = async (lat: number, lon: number) => {
    if (!chain) return
    setBusy(true)
    try {
      await onSubmit({
        lat,
        lon,
        chain: chain === "Inne / Nieznane" ? null : chain,
        name: name.trim() ? name.trim() : null,
      })
    } finally {
      setBusy(false)
    }
  }

  const onGo = () => {
    if (!pin) return
    void submitWith(pin.lat, pin.lon)
  }

  return (
    <section className="sheet" role="dialog" aria-label="Dodaj automat">
      <div className="grip" />
      <button className="close" onClick={onClose} aria-label="Zamknij">
        ← Anuluj
      </button>
      <h2>Dodaj automat</h2>
      {!pin && (
        <p className="meta">
          Wskaż lokalizację: dotknij miejsca na mapie <em>lub</em>{" "}
          <button
            onClick={useGeo}
            style={{ border: 0, background: "none", color: "var(--brand)", textDecoration: "underline", padding: 0, font: "inherit", cursor: "pointer" }}
          >
            użyj mojej lokalizacji
          </button>
          .
        </p>
      )}
      {pin && (
        <p className="meta">
          Wybrana lokalizacja: {pin.lat.toFixed(5)}, {pin.lon.toFixed(5)}
        </p>
      )}
      <div className="form-row">
        <label htmlFor="chain">Sieć</label>
        <select
          id="chain"
          value={chain}
          onChange={(e) => setChain(e.target.value)}
          disabled={busy}
        >
          <option value="">— wybierz —</option>
          {CHAINS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      <div className="form-row">
        <label htmlFor="name">Nazwa lokalizacji (opcjonalnie)</label>
        <input
          id="name"
          type="text"
          placeholder="np. Plac Wilsona"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
        />
      </div>
      <div className="actions">
        <button className="ok" onClick={onGo} disabled={!pin || !chain || busy}>
          {busy ? "Wysyłam…" : "Dodaj"}
        </button>
        <button className="broken" onClick={onClose} disabled={busy}>
          Anuluj
        </button>
      </div>
    </section>
  )
}
